'use server'

import path from 'path'
import { fileURLToPath } from 'url'

import { format } from 'date-fns'
import { Attachment } from 'nodemailer/lib/mailer'

import { RECAPTCHA_ACTIONS } from '@/constants'
import { Contact } from '@/database'
import {
    ContactDetailsSubmittedEmailTemplate,
    ContactDetailsSubmittedPlaceholders,
} from '@/templates/email/contact-details-submitted.email'

import action from '../handlers/action'
import handleError from '../handlers/error'
import dbConnect from '../mongoose'
import { verifyRecaptchaToken } from '../recaptcha'
import { sendEmail } from '../send-mail'
import { replaceTemplatePlaceholders } from '../utils'
import { ContactFormSchema } from '../validations'

export async function createContact(
    recaptchaToken: string | null,
    params: CreateContactParams
): Promise<ActionResponse<Contact>> {
    if (!recaptchaToken) {
        return handleError(
            new Error('reCAPTCHA token is missing')
        ) as ErrorResponse
    }

    const isValid = await verifyRecaptchaToken(
        recaptchaToken,
        RECAPTCHA_ACTIONS.CONTACT_FORM
    )
    if (!isValid) {
        return handleError(
            new Error('reCAPTCHA verification failed')
        ) as ErrorResponse
    }

    const validationResult = await action({
        params,
        schema: ContactFormSchema,
    })

    if (validationResult instanceof Error) {
        return handleError(validationResult) as ErrorResponse
    }

    const { name, email, phone, message } = validationResult.params!

    try {
        await dbConnect()

        const contact = await Contact.create({
            name,
            email,
            phone,
            message,
        })

        if (!contact) {
            throw new Error(`Failed to create contact`)
        }

        console.log('Contact created:', contact)

        const mailResponse = await sendAdminEmail(
            name,
            email,
            phone,
            message,
            contact.createdAt
        )

        if (!mailResponse?.messageId) {
            throw new Error('Failed to send contact admin email')
        }

        return { success: true, data: JSON.parse(JSON.stringify(contact)) }
    } catch (error) {
        return handleError(error) as ErrorResponse
    }
}

async function sendAdminEmail(
    contactName: string,
    contactEmail: string,
    contactPhone: string | undefined,
    contactMessage: string,
    contactCreatedAt: Date
) {
    const mailData: ContactDetailsSubmittedPlaceholders = {
        contactName,
        contactEmail,
        contactPhone: contactPhone || 'N/A',
        contactMessage: contactMessage.replace(/(?:\r\n|\r|\n)/g, '<br />'),
        contactCreatedAt: format(contactCreatedAt, 'MM/dd/yyyy hh:mm:ss a'),
    }

    const mailAttachments: Attachment[] = [
        {
            filename: 'site-logo.png',
            path: path.join(
                path.dirname(fileURLToPath(import.meta.url)),
                '../..',
                'public',
                'images',
                'site-logo.png'
            ),
            cid: 'site-logo',
        },
    ]

    const adminEmail = process.env.ADMIN_EMAIL!

    if (!adminEmail) {
        throw new Error('Admin email is not configured')
    }

    return await sendEmail(
        adminEmail,
        'New Contact Details Submitted: ' + contactName,
        replaceTemplatePlaceholders<ContactDetailsSubmittedPlaceholders>(
            ContactDetailsSubmittedEmailTemplate,
            mailData
        ),
        mailAttachments
    )
}
